import { Permission } from "../redux/models/permission.model";

// Permission names are `verb_object` keys (view_spas, assign_admin_role, can_send_promos).
// This turns them into something readable and sorts them into the area of the app they govern.

export type PermissionVerb = 'view' | 'create' | 'update' | 'delete' | 'assign' | 'manage' | 'other';

export interface PermissionGroup {
  key: string;
  label: string;
  permissions: DescribedPermission[];
}

export interface DescribedPermission {
  permission: Permission;
  key: string;        // raw name, e.g. assign_admin_role
  label: string;      // readable, e.g. "Assign admin role"
  verb: PermissionVerb;
  category: string;
}

// Matched in order: the first area whose pattern hits the key wins, so narrower areas
// (API, comments, firmware) come before the broad ones (spas, users).
const CATEGORIES: { label: string; test: RegExp }[] = [
  { label: 'Firmware', test: /firmware|upgrade_request/ },
  { label: 'API & Commands', test: /api|command/ },
  { label: 'Comments', test: /comment/ },
  { label: 'Notifications & Promos', test: /notif|promo/ },
  { label: 'Roles & Permissions', test: /role|permission/ },
  { label: 'User Groups', test: /user_group/ },
  { label: 'Dealerships', test: /dealer|corporate/ },
  { label: 'Logs & Diagnostics', test: /log|dev_controls|manual_mode|ampere/ },
  { label: 'Spas', test: /spa|register/ },
  { label: 'Users', test: /user|impersonate/ },
  { label: 'Dashboard', test: /dashboard/ },
];

const OTHER = 'Other';

// Display order of the groups (areas not listed sort after these)
const CATEGORY_ORDER = [
  'Dashboard', 'Spas', 'Users', 'Dealerships', 'Roles & Permissions', 'User Groups',
  'Comments', 'Notifications & Promos', 'Firmware', 'Logs & Diagnostics', 'API & Commands', OTHER,
];

const VERBS: Record<string, PermissionVerb> = {
  view: 'view', see: 'view', access: 'view',
  create: 'create', register: 'create', send: 'create', request: 'create',
  update: 'update', mark: 'update', enable: 'update', upgrade: 'update',
  delete: 'delete', deregister: 'delete', remove: 'delete',
  assign: 'assign', impersonate: 'assign',
  manage: 'manage',
};

export const categoryOf = (key: string): string =>
  CATEGORIES.find(({test}) => test.test(key))?.label ?? OTHER;

export const describePermission = (permission: Permission): DescribedPermission => {
  const key = (permission?.name ?? '').toLowerCase();
  const words = key.split('_').filter(Boolean);
  // "can_send_promos" reads as "Send promos"
  if (words[0] === 'can') words.shift();
  const verb = VERBS[words[0]] ?? 'other';
  const label = words.join(' ').replace(/^\w/, (c) => c.toUpperCase());
  return { permission, key, label, verb, category: categoryOf(key) };
};

// Grouped by area, in display order; permissions within a group sort by verb then name
const VERB_ORDER: PermissionVerb[] = ['view', 'create', 'update', 'delete', 'assign', 'manage', 'other'];

export const groupPermissions = (permissions: Permission[] = []): PermissionGroup[] => {
  const groups = new Map<string, DescribedPermission[]>();
  permissions.map(describePermission).forEach((described) => {
    const list = groups.get(described.category) ?? [];
    list.push(described);
    groups.set(described.category, list);
  });

  const order = (label: string) => {
    const index = CATEGORY_ORDER.indexOf(label);
    return index < 0 ? CATEGORY_ORDER.length : index;
  };

  return Array.from(groups.entries())
    .sort(([a], [b]) => order(a) - order(b) || a.localeCompare(b))
    .map(([label, list]) => ({
      key: label.toLowerCase().replace(/[^a-z]+/g, '-'),
      label,
      permissions: list.sort((a, b) =>
        VERB_ORDER.indexOf(a.verb) - VERB_ORDER.indexOf(b.verb) || a.label.localeCompare(b.label)),
    }));
};
